<template>
    <div v-if="yieldPublishers.length > 0">
        <LaBaseSelect
            data-test="yield-publishers-select"
            :items="yieldPublishers"
            :model-value="selectedPublishers"
            @update:model-value="onSelectionChange"
            itemValue="id"
            itemText="name"
            :stretch="false"
            :searchable="true"
            :multiple="true"
            :select-all-defaults="false"
        >
        </LaBaseSelect>
    </div>
</template>

<script>
import LaBaseSelect from "@/components/inputs/BaseSelect";
import { computed, watch } from "vue";
import store from "@/store/store";
import { getterTypes, actionTypes } from "@/store/Publisher/types";
import { useRoute, useRouter } from "vue-router";

export default {
    name: "YieldPublisherPicker",
    components: {
        LaBaseSelect,
    },
    setup() {
        const route = useRoute();
        const router = useRouter();

        // Get all publishers
        const publishers = computed(() => store.getters[getterTypes.getPublishers]);
        if (!publishers.value) {
            store.dispatch(actionTypes.getData);
        }

        // Filter only Yield publishers (is_yield = true)
        const yieldPublishers = computed(() => {
            if (!publishers.value) return [];
            return publishers.value.filter((p) => p.is_yield === true);
        });

        // Get selected publishers from Yield store (array)
        const selectedPublishers = computed(() => store.getters[getterTypes.getSelectedPublishersYield] || []);

        // Called when BaseSelect emits update:modelValue. Using an explicit handler
        // (instead of a computed setter via v-model) is more reliable — the setter
        // on a computed can be skipped when Vue's reactivity deems the new value
        // identity-equal to the current one.
        //
        // On dashboard-yield, URL mirroring is handled by DashboardYield's
        // [selectedPublishers] → router.replace watcher, so we only dispatch here.
        // On other routes that carry a :publisher param (websites, install, account, …)
        // the view owns its own URL→store sync; we swap the URL param so those views
        // refetch for the new publisher. Drop :website — it belongs to the previous one.
        const onSelectionChange = (val) => {
            const arr = Array.isArray(val) ? val : val ? [val] : [];
            store.dispatch(actionTypes.setSelectedPublishersYield, arr);

            if (route.name === "dashboard-yield" || !route.name) return;
            if (!("publisher" in (route.params || {}))) return;
            if (arr.length !== 1) return;

            const newParams = { ...route.params, publisher: String(arr[0].id) };
            delete newParams.website;
            router.push({ name: route.name, params: newParams }).catch(() => {});
        };

        // Sync store from URL when the user lands on a dashboard-yield route with a
        // concrete :publisher param (deep link / hard reload). Watch a function source
        // (not `route` directly) — Vue Router mutates params on the existing route
        // object on SPA navigation, so `watch(route, ...)` misses param-only changes.
        watch(
            () => [route.params.publisher, yieldPublishers.value],
            ([publisherParam, availablePublishers]) => {
                if (route.name !== "dashboard-yield" || !availablePublishers?.length) return;
                if (!publisherParam) return;

                const ids = String(publisherParam)
                    .split(",")
                    .map((id) => parseInt(id.trim()))
                    .filter((id) => !isNaN(id));
                const foundPublishers = availablePublishers.filter((p) => ids.includes(p.id));
                if (!foundPublishers.length) return;

                const currentIds = selectedPublishers.value.map((p) => p.id).sort();
                const newIds = foundPublishers.map((p) => p.id).sort();
                const needsUpdate =
                    currentIds.length !== newIds.length || !currentIds.every((id, i) => id === newIds[i]);

                if (needsUpdate) {
                    store.dispatch(actionTypes.setSelectedPublishersYield, foundPublishers);
                }
            },
            { immediate: true }
        );

        return {
            yieldPublishers,
            selectedPublishers,
            onSelectionChange,
        };
    },
};
</script>
